/** Global React error boundary — catches render-time errors and replaces
 *  the raw stack trace with a friendly recoverable panel. */
import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(err) {
    return { hasError: true, message: err?.message || "Unexpected error" };
  }

  componentDidCatch(err, info) {
    console.error("ErrorBoundary caught:", err, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false, message: "" });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-6" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-6">
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Something went wrong</div>
          <h1 className="mt-2 text-xl font-heading font-semibold text-slate-900">
            We couldn&apos;t render that view.
          </h1>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            The error has been logged. You can try again or head back to the home page —
            your data is safe.
          </p>
          <div className="mt-5 flex gap-2">
            <button onClick={this.reset}
              className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800"
              data-testid="error-boundary-retry">Try again</button>
            <a href="/" onClick={this.reset}
              className="h-9 px-3 text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 grid place-items-center"
              data-testid="error-boundary-home">Back to Overview</a>
          </div>
          {this.state.message && (
            <div className="mt-4 text-[11px] font-mono text-slate-400 truncate">
              {this.state.message}
            </div>
          )}
        </div>
      </div>
    );
  }
}
